import axios, { AxiosInstance } from 'axios';
import { isInactiveAccount } from './authErrors';
import {
  renewSignInAndResend,
  SignInRenewalFailed,
  signOutLocally,
} from './renewSignIn';
import { clearAllUnsentDrafts } from './unsentDrafts';
import {
  attachRequestId,
  noteApiResponse,
  rememberFailedRequest,
} from './requestId';

/**
 *
 * @param baseUrl - string, an URL to initiate all the requests. Optional.
 * @description API Handler to make calls torough Axios instance
 */

class ApiHandler {
  private axiosInstance: AxiosInstance;

  constructor(baseURL?: string) {
    // Default 5-minute timeout for normal endpoints. File-upload requests
    // already override per-request to FILE_UPLOAD_TIMEOUT (45 minutes) so
    // they are unaffected; this default just stops short calls from hanging
    // forever when the backend is unresponsive.
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 5 * 60 * 1000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Every call carries an X-Request-ID the server logs on every line, so
    // an error can name the call that failed (ZLAYT-1519).
    this.axiosInstance.interceptors.request.use(attachRequestId);

    this.axiosInstance.interceptors.response.use(
      (response) => {
        noteApiResponse(response);
        return response;
      },
      async (error) => {
        noteApiResponse(error?.response);
        rememberFailedRequest(error);
        // A network error or an axios timeout has no response. Reading
        // error.response.status on it threw a TypeError that replaced the
        // real error (ZLAYT-1453), so pass those through untouched.
        if (!error.response) {
          return Promise.reject(error);
        }
        if (
          error.response.status === 401 &&
          [
            'There is no user with this email',
            'This account is inactive.',
          ].includes(error.response.data?.message)
        ) {
          return Promise.reject(error);
        }
        if (
          error.response.status === 401 &&
          isInactiveAccount(error.response.data)
        ) {
          // ZLAYT-1645: an inactive account keeps no editor edits.
          clearAllUnsentDrafts();
          signOutLocally();
          window.location.href = '/sign-in';
          return Promise.reject(error);
        }
        // The backend reshapes simplejwt's 401 body; reading only the raw
        // one meant the renewal never ran and users were signed out at
        // expiry (ZLAYT-1649). Only a failed renewal signs the user out; a
        // resent request that fails for its own reason reaches the caller.
        try {
          const resent = await renewSignInAndResend(error, baseURL);
          if (resent) return resent;
        } catch (renewal) {
          if (!(renewal instanceof SignInRenewalFailed)) throw renewal;
          signOutLocally();
          window.location.href = '/sign-in';
        }
        return Promise.reject(error);
      }
    );

    this.initializeResponseInterceptor();

    this.setAuthToken(JSON.parse(localStorage.getItem('accessToken')!));
  }

  /**
   * @description Delete auth token from axios instance
   * @returns {boolean}
   */

  public removeAuthToken = async () => {
    return new Promise((resolve) => {
      delete this.axiosInstance.defaults.headers.common.Authorization;
      resolve(true);
    });
  };

  /**
   * @description Set auth token to axios instance
   * @returns {boolean}
   */

  public setAuthToken = (token: string | null) => {
    if (token && token !== 'null') {
      this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete this.axiosInstance.defaults.headers.common.Authorization;
    }
  };

  private initializeResponseInterceptor = () => {
    this.axiosInstance.interceptors.request.use((config) => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken && accessToken !== 'null') {
        config.headers.Authorization = `Bearer ${JSON.parse(accessToken)}`;
      }
      return config;
    });
    this.axiosInstance.interceptors.response.use(
      this.handleResponse,
      this.handleError
    );
  };

  // TODO: add types later
  private handleResponse = ({ data }: any) => data;

  private handleError = (error: any) => Promise.reject(error);

  /** @function
   * @name get
   * @param url - string, an URL to initiate all the requests. Optional.
   * @param config - object, an configuration to initiate all the requests. Optional.
   * @description Handler to make calls through Axios instance using the GET method
   */

  public get = <T>(url: string, config = {}) => {
    return this.axiosInstance.get<T>(url, config);
  };

  /** @function
   * @name post
   * @param url - string, an URL to initiate all the requests. Optional.
   * @param data - string, an data that will be sent to the backend. Optional.
   * @param config - object, an configuration to initiate all the requests. Optional.
   * @description Handler to make calls through Axios instance using the POST method
   */

  public post = <T>(url: string, data = {}, config: any = {}) => {
    // For file uploads (FormData), apply extended timeout
    const isFileUpload = data instanceof FormData;
    const FILE_UPLOAD_TIMEOUT = 45 * 60 * 1000; // 45 minutes for large files up to 500MB

    const timeoutConfig =
      isFileUpload && !config.timeout ? { timeout: FILE_UPLOAD_TIMEOUT } : {};

    return this.axiosInstance.post<T>(url, data, {
      ...timeoutConfig,
      ...config,
    });
  };

  /** @function
   * @name put
   * @param url - string, an URL to initiate all the requests. Optional.
   * @param data - string, an data that will be sent to the backend. Optional.
   * @param config - object, an configuration to initiate all the requests. Optional.
   * @description Handler to make calls through Axios instance using the PUT method
   */

  public put = <T>(url: string, data = {}, config = {}) =>
    this.axiosInstance.put<T>(url, data, config);

  /** @function
   * @name patch
   * @param url - string, an URL to initiate all the requests. Optional.
   * @param data - string, an data that will be sent to the backend. Optional.
   * @param config - object, an configuration to initiate all the requests. Optional.
   * @description Handler to make calls through Axios instance using the PATCH method
   */

  public patch = <T>(url: string, data = {}, config = {}) => {
    return this.axiosInstance.patch<T>(url, data, config);
  };

  /** @function
   * @name delete
   * @param url - string, an URL to initiate all the requests. Optional.
   * @param config - object, agit n configuration to initiate all the requests. Optional.
   * @description Handler to make calls through Axios instance using the DELETE method
   */

  public delete = (url: string, config = {}) =>
    this.axiosInstance.delete(url, config);

  /** @function
   * @name getAxiosInstance
   * @description Get the Axios instance
   */
  public getAxiosInstance = () => this.axiosInstance;
}

const apiHandler = new ApiHandler(import.meta.env.VITE_APP_API_URL);
export default apiHandler;
